import { Component, inject, effect, signal, WritableSignal, OnInit } from "@angular/core";
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators,FormArray, ValidatorFn, AbstractControl, ValidationErrors } from "@angular/forms";
import { FieldsetModule } from "primeng/fieldset";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { IftaLabelModule } from "primeng/iftalabel";
import { TextareaModule } from "primeng/textarea";
import { MessageModule } from "primeng/message";
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { ToastService } from '../../../../common/services/toast.service';
import { LessonsService } from "../../services/lessons.service";
import { LessonsToolsMappingService } from "../../../deeplink/services/lessons-tools-mapping.service";
import { ShellCoursesService } from "../../../shell-courses/services/shell-courses.service";
import { ShellCoursesDto } from "../../../shell-courses/dtos/shell-courses.dto";
import { CreateLessonDto } from "../../dtos/create-lesson.dto";

export type CanPostGradeToRLMS = {
    label: string;
    value: boolean;
}
@Component({
  selector: 'app-add-lessons',
  standalone: true,
  imports: [
    FieldsetModule,
    ButtonModule,
    InputTextModule,
    IftaLabelModule,
    TextareaModule,
    ReactiveFormsModule,
    RouterModule,
    MessageModule,
    FloatLabelModule,
    MultiSelectModule,
    CheckboxModule,
    SelectModule
  ],
  providers: [LessonsToolsMappingService, LessonsService,ShellCoursesService],
  templateUrl: './add-lessons.html'
})
export class AddLessonsComponent implements OnInit {
   private fb = inject(FormBuilder);
   private lessonsService = inject(LessonsService);
   private toastService = inject(ToastService);
   private shellCoursesService = inject(ShellCoursesService);
   private lessonsShadowMap:Map<string,CreateLessonDto> = new Map<string,CreateLessonDto>()
   router = inject(Router);
   shellCourses:Array<ShellCoursesDto> = new Array<ShellCoursesDto>()
   wasCheckAvailabilityButtonClicked = signal<boolean>(false);
   lessonsPreview = signal<string>('');
   presentCourseCodes = signal<string[]>([]);
   absentCourseCodes = signal<string[]>([]);
   canPostGradeToRLMSValues:Array<CanPostGradeToRLMS> = [{
        label:"Yes",
        value:true
      },
      {
        label:"No",
        value:false
      }
  ]
   addLessonsFormGroup: FormGroup = this.fb.group({
     selectedShellCourses:  [[], [Validators.required]],
     lessons: this.fb.array([
      this.fb.group({
        lesson:['THEORY',Validators.required],
        passingMarks:[70,[Validators.required,Validators.min(0)]],
        canPostGradeToRLMS:[false,[Validators.required]],
      }),
      this.fb.group({
        lesson:['EXAM',Validators.required],
        passingMarks:[70,[Validators.required,Validators.min(0)]],
        canPostGradeToRLMS:[true,[Validators.required]],
      }),
     ],{ validators: this.onlyOneLessonCanPostGrade() }),
   });

   private formValues = toSignal(this.addLessonsFormGroup.valueChanges, { initialValue: this.addLessonsFormGroup.value });
   
   
    constructor() {
     effect(() => {
       this.formValues(); // track signal — runs whenever any control changes
       this.showPreview();
     });
   }

   ngOnInit(): void {
     this.shellCoursesService.getAllShellCourses().then(data=>{
      this.shellCourses = data.data
    })
   }
  
   get lessons() :FormArray {
    return this.addLessonsFormGroup.get('lessons') as FormArray;
   }

   addLesson() {
    this.lessons.push(this.fb.group({
        lesson:['',Validators.required],
        passingMarks:[70,[Validators.required,Validators.min(0)]],
        canPostGradeToRLMS:[false,[Validators.required]],
      }));
   }
   removeLesson(index:number) {
     this.lessons.removeAt(index);
   }
   showPreview() {
     const { selectedShellCourses } = this.addLessonsFormGroup.value;
     const selectedShellCoursesArray: ShellCoursesDto[] = Array.isArray(selectedShellCourses) ? selectedShellCourses : [];
     this.lessonsShadowMap.clear();
     const lessonLines: string[] = [];
      if( selectedShellCoursesArray.length > 0 && this.lessons.value.length >0){
          for (let i = 0; i < selectedShellCoursesArray.length; i++) {
          const shellCourse = selectedShellCoursesArray[i];
          for (let j = 0; j < this.lessons.value.length; j++) {
            const lessonFormGroup = this.lessons.value[j];
            const lessonCourseCode = `${shellCourse.code}-${lessonFormGroup.lesson}`;
            if(shellCourse){
              this.lessonsShadowMap.set(lessonCourseCode,{
                shellCourse,
                lesson:lessonCourseCode,
                passingMarks:lessonFormGroup.passingMarks,
                canPostGradeToRLMS:lessonFormGroup.canPostGradeToRLMS
              });
              lessonLines.push(lessonCourseCode);
            }
          }
        }  
      }
      this.lessonsPreview.set(lessonLines.join(',\n'));
      this.wasCheckAvailabilityButtonClicked.set(false);
      this.presentCourseCodes.set([]);
      this.absentCourseCodes.set([]);
   }
   async checkAvailability(){
     try{
       if(this.lessonsPreview().trim() !== '' ){
        const lessonCourseCodes = this.lessonsPreview().split(",\n");
        const availableCourseCodesResult = await this.lessonsService.checkAvailabilityFor({courseCodes:lessonCourseCodes});
        this.presentCourseCodes.set(availableCourseCodesResult.present);
        this.absentCourseCodes.set(availableCourseCodesResult.absent);
        this.wasCheckAvailabilityButtonClicked.set(true);
       }else{
         this.toastService.show({ severity: 'error', summary: 'Error', detail: 'Nothing in preview,please check the form or select shell courses.' });
       }
     }catch(e){
       this.toastService.show({ severity: 'error', summary: 'Error', detail: 'There was a problem please contact tech support.' });
     }
    
   }
   
   async onSubmit() {
     if (this.addLessonsFormGroup.invalid) {
       this.addLessonsFormGroup.markAllAsTouched();
       this.toastService.show({ severity: 'error', summary: 'Error', detail: 'Please correct the errors in the form.' });
       return;
     }
     try {
       const lessonCourseCodes = this.absentCourseCodes();
       const lessonsToBeCreated:Array<CreateLessonDto> = new Array<CreateLessonDto>();
       lessonCourseCodes.forEach(lessonCourseCode=>{
        const lesson:CreateLessonDto = this.lessonsShadowMap.get(lessonCourseCode) as CreateLessonDto;
        if (!lesson) { return; }  
        lessonsToBeCreated.push(lesson)
       });
       await this.lessonsService.createLessons(lessonsToBeCreated);
       this.toastService.show({ severity: 'success', summary: 'Success', detail: 'Lessons created successfully.' });
       this.router.navigate(['/view-lessons']);
     } catch {
       this.toastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to create lessons. Please try again.' });
     } finally {
       this.wasCheckAvailabilityButtonClicked.set(false);
     }
   }
  onlyOneLessonCanPostGrade(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const lessons = control as FormArray;
      const count = lessons.controls
        .filter(c => c.get('canPostGradeToRLMS')?.value === true)
        .length;
      return count === 1 ? null : { postGradeCount: { actual: count } };
    };
  }

}
